import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Container, SectionHeading } from "@/components/primitives";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { PageHero, CTASection, ReviewsSection } from "@/components/sections";
import { useSeo } from "@/lib/seo";
import { services } from "@/data/site";

export default function Services() {
  useSeo({
    title: "Dental Services & Treatments | Meridian Dental Studio, Hell's Kitchen NYC",
    description: "Preventative, cosmetic, restorative and orthodontic dental services in Hell's Kitchen, Manhattan. Cleanings, veneers, implants, root canals and Invisalign. Book today.",
    path: "/services",
  });

  return (
    <>
      <PageHero
        eyebrow="Services & Treatments"
        title="Comprehensive care, all under one roof."
        intro="From routine cleanings to full smile makeovers and Invisalign — everything your family needs, delivered with precision and comfort in Hell's Kitchen."
      />

      <section className="pb-24 sm:pb-32">
        <Container>
          <Stagger className="grid gap-6">
            {services.map((s) => (
              <StaggerItem key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-card-${s.slug}`}
                  className="group grid overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] transition-colors hover:border-[var(--primary)] lg:grid-cols-[0.9fr_1.1fr]"
                >
                  <div className="relative aspect-[16/10] overflow-hidden lg:aspect-auto lg:min-h-[320px]">
                    <img src={s.hero} alt={s.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105" />
                    <span className="absolute left-5 top-5 font-mono text-sm text-white/90">{s.index}</span>
                  </div>
                  <div className="flex flex-col justify-center p-8 sm:p-12">
                    <div className="flex items-start justify-between gap-4">
                      <h2 className="font-heading text-3xl font-light tracking-tight text-[var(--fg)] sm:text-4xl">{s.name}</h2>
                      <ArrowUpRight size={26} className="shrink-0 text-[var(--muted-fg)] transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[var(--primary)]" />
                    </div>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-[var(--muted-fg)] sm:text-base">{s.short}</p>
                    <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2">
                      {s.treatments.map((t) => (
                        <li key={t.name} className="flex items-center gap-2 text-sm text-[var(--fg)]/80">
                          <Check size={14} className="shrink-0 text-[var(--primary)]" /> {t.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </section>

      <ReviewsSection />
      <CTASection />
    </>
  );
}
